import React from 'react';
import { isUndefined } from 'lodash-es';

import { CONFIGURATOR_DEFAULT_CURRENCY, CONFIGURATOR_ENGAGEMENT_STEP } from '../constants';
import {
  Campaign,
  CampaignItem,
  CampaignItemTier,
  CampaignItemType,
  ConfiguratorContextEngagementCampaign,
  ConfiguratorContextEngagementCampaignClickHandler,
  ConfiguratorContextEngagementItem,
  ConfiguratorContextEngagementTier,
  ConfiguratorEngagementCampaignFeature,
  ConfiguratorEngagementHtmlPreview,
  ConfiguratorEngagementItem,
  ConfiguratorEngagementItemClickHandler,
  ConfiguratorEngagementItemsMap,
  ConfiguratorEngagementTier,
  ConfiguratorEngagementTierClickHandler,
  ConfiguratorEngagementTiersMap,
  ConfiguratorEngagementVideo,
  ConfiguratorSessionState,
  ConfiguratorStep,
  DefaultItem,
  EngagementOffer,
  SetSessionCallback,
} from '../types';
import {
  buildCustomCampaign,
  CONFIGURATOR_ENGAGEMENT_CUSTOM_CAMPAIGN_ID,
  computeCampaignFeatures,
  computeCampaignOffer,
  getActiveCampaign,
  getArtBundleById,
  getGamificationItems,
  getHighlights,
  getHighlightsHeader,
  getHtmlPreview,
  isEngagementSessionDataEmpty,
  isGamificationItem,
} from '../utils';

export function itemsMatchCampaignDefaults(
  sessionItems: DefaultItem[],
  defaultItems: DefaultItem[],
): boolean {
  if (sessionItems.length !== defaultItems.length) return false;
  return defaultItems.every((def) =>
    sessionItems.some(
      (si) => si.item_id === def.item_id && String(si.tier_id) === String(def.tier_id) && si.type === def.type,
    ),
  );
}

export function useEngagementStep(
  apiItems: ConfiguratorEngagementItem[] = [],
  apiTiers: ConfiguratorEngagementTier[] = [],
  apiCampaigns: Campaign[] = [],
  sessionState: ConfiguratorSessionState = {},
  setSession: SetSessionCallback,
  step?: ConfiguratorStep,
  currency: string = CONFIGURATOR_DEFAULT_CURRENCY,
) {
  const [selectedItem, setSelectedItem] = React.useState<ConfiguratorEngagementItem | undefined>();
  const [openSelectModal, setOpenSelectModal] = React.useState<boolean>(false);

  const campaign = React.useMemo<Campaign | undefined>(() => {
    if (sessionState.campaignId) {
      return apiCampaigns.find((c) => String(c.id) === String(sessionState.campaignId));
    }
    return getActiveCampaign(apiCampaigns, sessionState);
  }, [apiCampaigns, sessionState]);

  const campaignItems = React.useMemo<CampaignItem[]>(() => {
    if (isUndefined(campaign)) {
      return [];
    }

    return getGamificationItems(campaign.items);
  }, [campaign]);

  const isCustomPack = React.useMemo<boolean>(() => {
    if (!campaign?.default_items?.length || !sessionState.items?.length) return false;
    const gamificationDefaultItems = campaign.default_items.filter(
      (i) => i.type === CampaignItemType.GAMIFICATION_ITEM,
    );
    if (!gamificationDefaultItems.length) return false;
    // Custom if any campaign default item is absent or on a different tier in the session.
    // Extra items added by the engagement step for non-default slots are intentionally ignored.
    return !gamificationDefaultItems.every((def) =>
      sessionState.items?.some(
        (si) => si.item_id === def.item_id && String(si.tier_id) === String(def.tier_id) && si.type === def.type,
      ),
    );
  }, [campaign, sessionState.items]);

  const engagementOffer = React.useMemo<EngagementOffer | undefined>(() => {
    if (!campaign?.price_evaluation) return undefined;
    const { basePrice, discountedPrice } = campaign.price_evaluation;
    const hasDiscount = discountedPrice > 0 && discountedPrice < basePrice;
    return {
      price: hasDiscount ? discountedPrice : basePrice,
      originalPrice: hasDiscount ? basePrice : undefined,
      currency,
      isCustom: isCustomPack,
    };
  }, [campaign, isCustomPack, currency]);

  const items = React.useMemo<ConfiguratorEngagementItem[]>(() => {
    return apiItems.filter((item) => item.is_active).toSorted((a, b) => a.priority - b.priority);
  }, [apiItems]);

  const itemsMap = React.useMemo<ConfiguratorEngagementItemsMap>(() => {
    return items.reduce<ConfiguratorEngagementItemsMap>((map, currentItem) => {
      map[currentItem.id] = currentItem;

      return map;
    }, {});
  }, [items]);

  const tiers = React.useMemo<ConfiguratorEngagementTier[]>(() => {
    return apiTiers.filter((tier) => tier.is_active).toSorted((a, b) => a.priority - b.priority);
  }, [apiTiers]);

  const tiersMap = React.useMemo<ConfiguratorEngagementTiersMap>(() => {
    return tiers.reduce<ConfiguratorEngagementTiersMap>((map, currentTier) => {
      map[currentTier.id] = currentTier;

      return map;
    }, {});
  }, [tiers]);

  React.useEffect(() => {
    if (step?.id === CONFIGURATOR_ENGAGEMENT_STEP.id && isUndefined(selectedItem)) {
      const sessionGamificationItems = getGamificationItems(sessionState.items);
      const firstSessionItem = sessionGamificationItems[0];

      if (!isUndefined(firstSessionItem) && !isUndefined(itemsMap[firstSessionItem.item_id])) {
        setSelectedItem(itemsMap[firstSessionItem.item_id]);
      }
    }
  }, [sessionState, itemsMap, selectedItem, step]);

  const handleSelectModalToggle = React.useCallback(() => {
    setOpenSelectModal((prevState) => !prevState);
  }, []);

  const handleSelectItem = React.useCallback<ConfiguratorEngagementItemClickHandler>((nextItem) => {
    setSelectedItem(nextItem);
  }, []);

  const handleSelectTier = React.useCallback<ConfiguratorEngagementTierClickHandler>(
    (nextItem, nextTier) => {
      const newItems = !isUndefined(sessionState.items) ? [...sessionState.items] : [];
      const tierIndex = newItems.findIndex((item) => isGamificationItem(item) && item.item_id === nextItem.id);

      if (tierIndex > -1) {
        if (!isUndefined(newItems[tierIndex])) {
          newItems[tierIndex] = {
            ...newItems[tierIndex],
            item_id: nextItem.id,
            tier_id: nextTier.id,
          };
        }
      } else {
        newItems.push({
          type: CampaignItemType.GAMIFICATION_ITEM,
          item_id: nextItem.id,
          tier_id: nextTier.id,
        });
      }

      setSelectedItem(nextItem);

      setSession({ items: newItems });
    },
    [sessionState.items, setSession],
  );

  const handleCampaignClick = React.useCallback<ConfiguratorContextEngagementCampaignClickHandler>(
    (nextCampaign) => {
      setSession({
        campaignId: nextCampaign.id,
      });
    },
    [setSession],
  );

  const customCampaign = React.useMemo<Campaign | undefined>(
    () => buildCustomCampaign(campaign, sessionState.items, engagementOffer),
    [campaign, sessionState.items, engagementOffer],
  );

  const contextCampaigns = React.useMemo<ConfiguratorContextEngagementCampaign[]>(() => {
    const realEntries: ConfiguratorContextEngagementCampaign[] = apiCampaigns.map((c) => ({
      ...c,
      active: !isCustomPack && String(c.id) === String(sessionState.campaignId),
      onClick: () => handleCampaignClick(c),
      items: c.items.map((campaignItem) => ({
        ...campaignItem,
        active: false, // TODO
        onClick: () => undefined, // TODO
      })),
      offer: computeCampaignOffer(c, false, currency),
      isCustom: false,
    }));

    if (!customCampaign) return realEntries;

    const customEntry: ConfiguratorContextEngagementCampaign = {
      ...customCampaign,
      active: isCustomPack,
      // No-op click: the Custom entry reflects the user's current item picks
      // and becomes "active" automatically when isCustomPack is true.
      onClick: () => undefined,
      items: customCampaign.items.map((campaignItem) => ({
        ...campaignItem,
        active: false,
        onClick: () => undefined,
      })),
      offer: computeCampaignOffer(customCampaign, true, currency),
      isCustom: true,
    };

    return [...realEntries, customEntry];
  }, [apiCampaigns, customCampaign, isCustomPack, sessionState.campaignId, handleCampaignClick, currency]);

  // Drawer state — independent of which campaign is confirmed.
  const [drawerCampaignId, setDrawerCampaignId] = React.useState<string | undefined>();

  const handleDrawerOpen = React.useCallback((id: string) => {
    setDrawerCampaignId(id);
  }, []);

  const handleDrawerClose = React.useCallback(() => {
    setDrawerCampaignId(undefined);
  }, []);

  const drawerCampaign = React.useMemo<ConfiguratorContextEngagementCampaign | undefined>(() => {
    if (isUndefined(drawerCampaignId)) return undefined;
    return contextCampaigns.find((c) => String(c.id) === String(drawerCampaignId));
  }, [contextCampaigns, drawerCampaignId]);

  const drawerFeatures = React.useMemo<ConfiguratorEngagementCampaignFeature[]>(() => {
    if (isUndefined(drawerCampaign)) return [];
    // The Custom entry's items array was rebuilt from session selections; for real
    // entries we fall through to the same util which keys off default_items.
    const source: Campaign =
      drawerCampaign.id === CONFIGURATOR_ENGAGEMENT_CUSTOM_CAMPAIGN_ID && customCampaign
        ? customCampaign
        : drawerCampaign;
    return computeCampaignFeatures(source, apiItems);
  }, [drawerCampaign, customCampaign, apiItems]);

  const contextItems = React.useMemo<ConfiguratorContextEngagementItem[]>(() => {
    if (Object.keys(itemsMap).length === 0 || Object.keys(tiersMap).length === 0) {
      return [];
    }

    return campaignItems.map((campaignItem) => {
      const sessionItem = sessionState.items?.find((sessionItem) => {
        return isGamificationItem(sessionItem) && sessionItem.item_id === campaignItem.id;
      });

      const item = itemsMap[campaignItem.id];
      const tiers: ConfiguratorContextEngagementTier[] = [];

      let activeCampaignTier: CampaignItemTier | undefined;

      campaignItem.tiers.forEach((campaignTier) => {
        const tier = tiersMap[campaignTier.id];
        const isTierActive = !isUndefined(sessionItem) ? sessionItem.tier_id === tier?.id : false;

        if (isTierActive) {
          activeCampaignTier = campaignTier;
        }

        tiers.push({
          ...tier,
          active: isTierActive,
          onClick: () => handleSelectTier(item, tier),
          enabled: campaignTier.enabled,
          base_price: campaignTier.base_price,
          discounted_price: campaignTier.discounted_price,
        });
      });

      tiers.sort((a, b) => a.priority - b.priority);

      const currency_code = currency;
      const { base_price = 0, discount_key, discounted_price } = activeCampaignTier ?? {};
      const tierHasDiscount = discounted_price !== undefined && discounted_price > 0 && discounted_price < base_price;

      const discountKeyArtBundle = getArtBundleById(discount_key?.art_bundle, 'discount_key');
      const tierArtBundle = getArtBundleById(tiersMap[activeCampaignTier?.id ?? 0]?.art_bundle, 'tier');

      return {
        ...item,
        tiers,
        active: item.id === selectedItem?.id,
        onClick: () => handleSelectItem(item),
        price: {
          currency: currency_code,
          totalCost: base_price,
          discount: (tierHasDiscount && !isCustomPack) ? discountKeyArtBundle?.text_name : undefined,
          cost: (tierHasDiscount && !isCustomPack) ? discounted_price : base_price,
        },
        showPrice: tierArtBundle?.properties?.showPrice ?? true,
      };
    });
  }, [sessionState, campaignItems, selectedItem, itemsMap, tiersMap, isCustomPack, handleSelectItem, handleSelectTier, currency]);

  React.useEffect(() => {
    if (step?.id === CONFIGURATOR_ENGAGEMENT_STEP.id && isEngagementSessionDataEmpty(sessionState, campaignItems)) {
      const sessionGamificationItems = getGamificationItems(sessionState.items);
      const campaignGamificationItems = getGamificationItems(campaignItems);

      if (sessionGamificationItems.length < campaignGamificationItems.length) {
        const remainingItems = campaignGamificationItems.filter((campaignItem) => {
          return !sessionGamificationItems.some((sessionItem) => sessionItem.item_id === campaignItem.id);
        });

        const newItems = !isUndefined(sessionState.items) ? [...sessionState.items] : [];

        remainingItems.forEach((remainingItem) => {
          const tiers = remainingItem.tiers.toSorted((a, b) => tiersMap[a.id]?.priority - tiersMap[b.id]?.priority);

          newItems.push({
            type: remainingItem.type,
            item_id: remainingItem.id,
            tier_id: tiers[0].id,
          });
        });

        setSession({
          items: newItems,
        });
      }
    }
  }, [sessionState, step, campaignItems, tiersMap, setSession]);

  const { contextHighlights, contextHighlightsHeader } = React.useMemo(() => {
    const sessionItem = sessionState.items?.find((sessionItem) => sessionItem.item_id === selectedItem?.id);
    const selectedTier = !isUndefined(sessionItem) ? tiersMap[sessionItem.tier_id] : undefined;

    if (isUndefined(selectedTier)) {
      return {
        contextHighlights: [],
        contextHighlightsHeader: undefined,
      };
    }

    return {
      contextHighlights: getHighlights(selectedTier),
      contextHighlightsHeader: getHighlightsHeader(selectedTier),
    };
  }, [sessionState, selectedItem, tiersMap]);

  const engagementVideos = React.useMemo<ConfiguratorEngagementVideo[]>(() => {
    const videos: ConfiguratorEngagementVideo[] = [];

    // Iterate every tier (not just the ones listed in item.cost) so tiers without a
    // cost entry — most notably the "off"/level-0 tier — still contribute their video.
    for (const tier of tiers) {
      const itemId = (tier as any).gamification_item_id;
      if (isUndefined(itemId)) continue;

      const videoArtBundle = tier.art_bundle.find((ab) => ab?.properties?.section_id === 'video');
      const src = videoArtBundle?.properties?.src ?? '';
      if (!src) continue;

      videos.push({
        ...videoArtBundle?.properties,
        key: `${itemId}_${tier.id}`,
        itemId,
        tierId: tier.id,
        src,
      });
    }

    return videos;
  }, [tiers]);

  const engagementSelectedVideo = React.useMemo<ConfiguratorEngagementVideo | undefined>(() => {
    const sessionItem = sessionState.items?.find((sessionItem) => sessionItem.item_id === selectedItem?.id);
    const selectedTier = !isUndefined(sessionItem) ? tiersMap[sessionItem.tier_id] : undefined;

    if (isUndefined(selectedTier)) {
      return;
    }

    return engagementVideos.find(
      (video) => video.itemId === selectedTier.gamification_item_id && video.tierId === selectedTier.id,
    );
  }, [sessionState, selectedItem, tiersMap, engagementVideos]);

  const engagementHtmlPreviews = React.useMemo<ConfiguratorEngagementHtmlPreview[]>(() => {
    const previews: ConfiguratorEngagementHtmlPreview[] = [];

    for (const item of items) {
      for (const cost of item.cost) {
        const tier = tiersMap[cost.gamification_tier_id];
        if (isUndefined(tier)) continue;

        const preview = getHtmlPreview(tier, item.id, tier.id);
        if (preview) {
          previews.push(preview);
        }
      }
    }

    return previews;
  }, [items, tiersMap]);

  const engagementSelectedHtml = React.useMemo<ConfiguratorEngagementHtmlPreview | undefined>(() => {
    const sessionItem = sessionState.items?.find((sessionItem) => sessionItem.item_id === selectedItem?.id);
    const selectedTier = !isUndefined(sessionItem) ? tiersMap[sessionItem.tier_id] : undefined;

    if (isUndefined(selectedTier)) {
      return;
    }

    return engagementHtmlPreviews.find(
      (preview) => preview.itemId === selectedTier.gamification_item_id && preview.tierId === selectedTier.id,
    );
  }, [sessionState, selectedItem, tiersMap, engagementHtmlPreviews]);

  const marketScanPackName = React.useMemo<string | undefined>(() => {
    if (!sessionState.campaignId) return undefined;
    return campaign?.name;
  }, [campaign, sessionState.campaignId]);

  return React.useMemo(
    () => ({
      engagementCampaign: campaign,
      engagementCampaigns: contextCampaigns,
      engagementItem: selectedItem,
      engagementItems: contextItems,
      engagementHighlights: contextHighlights,
      engagementHighlightsHeader: contextHighlightsHeader,
      engagementVideos,
      engagementSelectedVideo,
      engagementHtmlPreviews,
      engagementSelectedHtml,
      engagementOpenSelectModal: openSelectModal,
      onEngagementSelectModalToggle: handleSelectModalToggle,
      marketScanPackName,
      engagementOffer,
      engagementIsCustomPack: isCustomPack,
      engagementDrawerCampaignId: drawerCampaignId,
      onEngagementDrawerOpen: handleDrawerOpen,
      onEngagementDrawerClose: handleDrawerClose,
      engagementDrawerCampaign: drawerCampaign,
      engagementDrawerFeatures: drawerFeatures,
    }),
    [
      campaign,
      contextCampaigns,
      selectedItem,
      contextItems,
      contextHighlights,
      contextHighlightsHeader,
      engagementVideos,
      engagementSelectedVideo,
      engagementHtmlPreviews,
      engagementSelectedHtml,
      openSelectModal,
      handleSelectModalToggle,
      marketScanPackName,
      engagementOffer,
      isCustomPack,
      drawerCampaignId,
      handleDrawerOpen,
      handleDrawerClose,
      drawerCampaign,
      drawerFeatures,
    ],
  );
}
